import type { LucideIcon } from "lucide-react";
import {
  Activity,
  BarChart3,
  BookOpen,
  Bot,
  Building2,
  Calendar,
  Heart,
  Home,
  Layout,
  Library,
  LifeBuoy,
  MessageSquare,
  Network,
  Globe,
  LayoutDashboard,
  Lightbulb,
  LineChart,
  MessageSquareReply,
  Plus,
  Coins,
  Target,
  TrendingUp,
  Users,
  Video,
} from "lucide-react";

export interface NavItemChild {
  name: string;
  href: string;
  icon: LucideIcon;
  tooltip?: string;
  badge?: string;
}

export interface NavItemWithChildren {
  name: string;
  icon: LucideIcon;
  tooltip?: string;
  children: NavItemChild[];
}

export interface NavItemLink {
  name: string;
  href: string;
  icon: LucideIcon;
  tooltip?: string;
  badge?: string;
  external?: boolean;
}

export interface NavItemDivider {
  type: "divider";
  label?: string;
  /** Seção recolhível na sidebar expandida (aberta ao carregar). */
  collapsible?: boolean;
}

export type NavItem = NavItemLink | NavItemWithChildren | NavItemDivider;

export function isNavItemWithChildren(
  item: NavItem,
): item is NavItemWithChildren {
  return (
    "children" in item && Array.isArray((item as NavItemWithChildren).children)
  );
}

export const NAV_ROUTES = {
  POSTS: [
    "/posts",
    "/posts/ai-schedule",
    "/posts/calendar",
    "/posts/insights",
    "/posts/video-studio",
  ],
  ADS: ["/ads/new", "/ads", "/ads/insights"],
  CONTENT: ["/content", "/content/ebooks", "/content/insights"],
  ORGANIZATION: ["/organization"],
} as const;

export const navigation: NavItem[] = [
  {
    name: "Dashboard",
    href: "/",
    icon: LayoutDashboard,
    tooltip: "Resumo dos seus posts, anúncios e atividades",
  },
  { type: "divider", label: "Publicar" },
  {
    name: "Conteúdo",
    icon: Library,
    tooltip: "Textos, links e ebooks que servem de base para os posts",
    children: [
      {
        name: "Biblioteca",
        href: "/content",
        icon: Library,
        tooltip: "Textos e links salvos para usar nos posts",
      },
      {
        name: "Ebooks",
        href: "/content/ebooks",
        icon: BookOpen,
        tooltip: "Crie e organize seus ebooks",
      },
      {
        name: "Insights",
        href: "/content/insights",
        icon: BarChart3,
        tooltip: "Uso e desempenho da biblioteca e dos ebooks",
      },
    ],
  },
  {
    name: "Posts (Orgânico)",
    icon: Layout,
    tooltip: "Crie, agende e acompanhe posts nas redes",
    children: [
      {
        name: "Postagens",
        href: "/posts",
        icon: Layout,
        tooltip: "Todos os posts, com filtros por status",
      },
      {
        name: "Estúdio de Vídeo & Voz",
        href: "/posts/video-studio",
        icon: Video,
        tooltip: "Clone sua voz e gere vídeos a partir de roteiros",
        badge: "BETA",
      },
      {
        name: "Cronograma IA",
        href: "/posts/ai-schedule",
        icon: Bot,
        tooltip: "Sugestões de posts geradas pela IA",
      },
      {
        name: "Calendário",
        href: "/posts/calendar",
        icon: Calendar,
        tooltip: "Posts agendados, dia a dia",
      },
      {
        name: "Insights",
        href: "/posts/insights",
        icon: BarChart3,
        tooltip: "Desempenho dos posts por rede",
      },
    ],
  },
  {
    name: "Anúncios (Pago)",
    icon: TrendingUp,
    tooltip: "Crie e acompanhe campanhas pagas",
    children: [
      {
        name: "Novo Anúncio",
        href: "/ads/new",
        icon: Plus,
        tooltip: "Crie uma campanha no Meta ou no Google",
      },
      {
        name: "Anúncios",
        href: "/ads",
        icon: BarChart3,
        tooltip: "Campanhas ativas, gastos e resultados",
      },
      {
        name: "Insights",
        href: "/ads/insights",
        icon: BarChart3,
        tooltip: "Conversões, custo por lead e retorno",
      },
    ],
  },
  {
    name: "Respostas Automáticas",
    href: "/auto-responder",
    icon: MessageSquareReply,
    tooltip: "IA que responde comentários e mensagens",
  },
  { type: "divider", label: "Público e vendas" },
  {
    name: "Gerenciar Leads",
    href: "/leads",
    icon: Users,
    tooltip: "Leads vindos dos anúncios e do LeadFlow",
  },
  {
    name: "Mapa de Empatia",
    href: "/empathy-mapping",
    icon: Heart,
    tooltip: "Personas e perfil de cliente ideal (ICP)",
  },
  {
    name: "Inteligência de Receita",
    href: "/revenue-intelligence",
    icon: Activity,
    tooltip: "Leads mais quentes e campanhas que geram vendas",
  },
  { type: "divider", label: "Inteligência" },
  {
    name: "Estratégia de Marketing",
    href: "/marketing-strategy",
    icon: Lightbulb,
    tooltip: "Recomendações da IA para orgânico e pago",
  },
  {
    name: "Analytics & IA",
    href: "/analytics",
    icon: LineChart,
    tooltip: "Tendências, sentimento e hashtags do seu conteúdo",
  },
  { type: "divider", label: "Apps Virtus", collapsible: true },
  {
    name: "Virtus LeadFlow",
    href: "/apps/leadflow",
    icon: Network,
    tooltip: "Capta leads dos anúncios e envia aos CRMs · abre com seu login",
  },
  {
    name: "Virtus Chat",
    href: "/apps/chat",
    icon: MessageSquare,
    tooltip: "CRM de WhatsApp · abre com seu login",
  },
  {
    name: "Virtus Lead B2B",
    href: "/apps/lead-b2b",
    icon: Target,
    tooltip: "Prospecção e funil de vendas B2B · abre com seu login",
  },
  {
    name: "Virtus Site Builder",
    href: "/apps/site-builder",
    icon: Globe,
    tooltip: "Landing pages criadas com IA · abre com seu login",
  },
  {
    name: "Virtus Insights",
    href: "/apps/insights",
    icon: BarChart3,
    tooltip: "Indicadores por empresa, filial e cliente · abre com seu login",
  },
];

const ROUTE_TITLES: { check: (p: string) => boolean; title: string }[] = [
  { check: (p) => p === "/", title: "Dashboard" },
  { check: (p) => p === "/content" || p === "/content/", title: "Biblioteca" },
  { check: (p) => p === "/content/ebooks", title: "Ebooks" },
  {
    check: (p) => p.startsWith("/content/insights"),
    title: "Insights",
  },
  { check: (p) => p.startsWith("/posts/ai-schedule"), title: "Cronograma IA" },
  { check: (p) => p.startsWith("/posts/calendar"), title: "Calendário" },
  {
    check: (p) => p.startsWith("/posts/video-studio"),
    title: "Estúdio de Vídeo & Voz",
  },
  {
    check: (p) => p.startsWith("/posts/insights"),
    title: "Insights",
  },
  { check: (p) => p === "/posts" || p === "/posts/", title: "Postagens" },
  { check: (p) => p.startsWith("/ads/new"), title: "Novo Anúncio" },
  { check: (p) => p === "/ads" || p === "/ads/", title: "Anúncios" },
  {
    check: (p) => p.startsWith("/marketing-strategy"),
    title: "Estratégia de Marketing",
  },
  { check: (p) => p.startsWith("/leads"), title: "Gerenciar Leads" },
  {
    check: (p) => p.startsWith("/ads/insights"),
    title: "Insights",
  },
  { check: (p) => p.startsWith("/posts"), title: "Redes Sociais" },
  { check: (p) => p.startsWith("/analytics"), title: "Analytics & IA" },
  {
    check: (p) => p.startsWith("/revenue-intelligence"),
    title: "Inteligência de Receita",
  },
  { check: (p) => p.startsWith("/apps/leadflow"), title: "Virtus LeadFlow" },
  { check: (p) => p.startsWith("/apps/chat"), title: "Virtus Chat" },
  { check: (p) => p.startsWith("/apps/lead-b2b"), title: "Virtus Lead B2B" },
  {
    check: (p) => p.startsWith("/apps/site-builder"),
    title: "Virtus Site Builder",
  },
  { check: (p) => p.startsWith("/apps/insights"), title: "Virtus Insights" },
  {
    check: (p) => p.startsWith("/auto-responder"),
    title: "Respostas Automáticas",
  },
  { check: (p) => p.startsWith("/empathy-mapping"), title: "Mapa de Empatia" },

  {
    check: (p) => p === "/organization" || p === "/organization/",
    title: "Minha Organização",
  },
  {
    check: (p) => p.startsWith("/organization/clients/new"),
    title: "Nova Empresa Cliente",
  },
  {
    check: (p) => Boolean(p.match(/^\/organization\/clients\/[^/]+$/)),
    title: "Detalhes da Empresa",
  },
  {
    check: (p) => p.startsWith("/organization/clients"),
    title: "Empresas Cliente",
  },
  {
    check: (p) => p.startsWith("/organization/users"),
    title: "Gerenciar Usuários",
  },
  { check: (p) => p.startsWith("/organization"), title: "Minha Organização" },
  { check: (p) => p.startsWith("/credits"), title: "Créditos e consumo" },
  { check: (p) => p.startsWith("/support"), title: "Ajuda & Suporte" },
  { check: (p) => p.startsWith("/profile"), title: "Perfil" },
  { check: (p) => p.startsWith("/auth"), title: "Autenticação" },
];

export function isCurrentPage(pathname: string, href: string): boolean {
  if (href === "/") return pathname === "/";
  if (href === "/organization")
    return pathname === "/organization" || pathname === "/organization/";
  if (href === "/organization/clients")
    return pathname.startsWith("/organization/clients");
  if (href === "/content")
    return pathname === "/content" || pathname === "/content/";
  if (href === "/content/ebooks" || href === "/content/insights")
    return pathname === href || pathname === `${href}/`;
  // Listas na raiz do grupo (/posts, /ads) e subpáginas: só o caminho exato
  if (href === "/posts" || href === "/ads")
    return pathname === href || pathname === `${href}/`;
  if (href.startsWith("/posts/") || href.startsWith("/ads/"))
    return pathname === href || pathname === `${href}/`;

  if (pathname === href || pathname === `${href}/`) return true;

  if (
    pathname.startsWith(href) &&
    !(
      href === "/content" &&
      (pathname.startsWith("/content/ebooks") ||
        pathname.startsWith("/content/insights"))
    ) &&
    !(href === "/posts" && pathname !== "/posts" && pathname !== "/posts/")
  ) {
    return true;
  }
  return false;
}

export function getPageTitle(pathname: string): string {
  const match = ROUTE_TITLES.find(({ check }) => check(pathname));
  return match ? match.title : "";
}

// Grupo da sidebar exibido no breadcrumb do header ("Posts (Orgânico) › Insights").
const PAGE_PARENTS: [prefix: string, parent: string][] = [
  ["/content", "Conteúdo"],
  ["/posts", "Posts (Orgânico)"],
  ["/ads", "Anúncios (Pago)"],
  ["/organization", "Organização"],
];

export function getPageParent(pathname: string): string | null {
  const match = PAGE_PARENTS.find(
    ([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
  return match ? match[1] : null;
}

/**
 * Ícone do título da página no header, lido da própria navegação: item direto
 * usa o seu ícone; subitem usa o do grupo (é o que aparece ao lado do nome do
 * grupo no breadcrumb). Fora da navegação principal, regras próprias.
 */
export function getPageIcon(pathname: string): LucideIcon {
  for (const item of navigation) {
    if ("type" in item) continue;
    const matches = isNavItemWithChildren(item)
      ? item.children.some((c) => isCurrentPage(pathname, c.href))
      : isCurrentPage(pathname, item.href);
    if (matches) return item.icon;
  }

  // Rotas de um grupo que não são subitens
  const parent = getPageParent(pathname);
  const group = navigation.find(
    (item) => isNavItemWithChildren(item) && item.name === parent,
  ) as NavItemWithChildren | undefined;
  if (group) return group.icon;

  if (pathname.startsWith("/organization")) return Building2;
  if (pathname.startsWith("/credits")) return Coins;
  if (pathname.startsWith("/support")) return LifeBuoy;
  return Home;
}
